import type { Node } from "@xyflow/react";
import { articlesBatchA } from "./data/articles-batch-a";
import { articlesBatchB } from "./data/articles-batch-b";

// ═══ BAND 5 · ARTICLE RESEARCH — REAL PAGE SCREENSHOTS ═════════════════════════
// The full reference article wall: every publisher from BATCH A (operators /
// thought-leaders / media — Dev1) + BATCH B (AI companies / analysts / labs —
// Dev2), each rendered as a per-company column of clickable `articleShot` nodes
// over their real Playwright captures (1280x2000 @2x, hosted on R2). Grouped by
// company with a column header. Sits top-right, beside the Strategy & Research
// zone, as one clean top-aligned row of columns.
// Data: bands/data/articles-batch-a.ts + bands/data/articles-batch-b.ts.

type Shot = { title: string; url: string; img: string; domain: string };
type Col = { company: string; tag: string; blurb: string; articles: Shot[] };

const domainOf = (u: string) => {
  try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return ""; }
};

// Normalize both batches into one column list. Batch A carries a `tag`; batch B
// has none. Neither carries a blurb or per-shot domain — derive domain from URL.
const norm = (
  list: { company: string; tag?: string; articles: { title: string; url: string; img: string }[] }[],
): Col[] =>
  list.map((c) => ({
    company: c.company,
    tag: c.tag ?? "",
    blurb: "",
    articles: c.articles.map((a) => ({ title: a.title, url: a.url, img: a.img, domain: domainOf(a.url) })),
  }));

const COMPANIES: Col[] = [...norm(articlesBatchA), ...norm(articlesBatchB)];

export function band5Articles(): Node[] {
  const out: Node[] = [];
  const nCompanies = COMPANIES.length;
  const nShots = COMPANIES.reduce((n, c) => n + c.articles.length, 0);

  // Top-right, same top level as Strategy & Research (its right edge is ~8940);
  // X0 leaves ~1560px of air between the two zones. ONE clean row of per-company
  // columns, all sharing the same top edge so the wall scans left-to-right with
  // no overlap onto any other band (every other band lives at x < ~8940).
  const X0 = 10500, Y = -1300;
  const W = 440;          // article-shot width (readable when zoomed)
  const COL_PITCH = 600;  // 440 shot + 160 gutter — generous column separation
  const HEADER_H = 300;   // company-header block before the first shot (aligns all first images)
  const ROW_PITCH = 760;  // header(~70) + uniform image(640) + ~50 gap — aligned rows, no overlap

  out.push({
    id: "band-articles", type: "sectionTitle", position: { x: X0, y: Y },
    data: { title: "Article Research", sub: "the real reference pages — read the best publishers on the canvas, then pick our angles", band: true },
    draggable: false, selectable: false,
  });
  out.push({
    id: "sec-articles", type: "sectionTitle", position: { x: X0, y: Y + 700 },
    data: { title: "Reference article wall", sub: `${nCompanies} publishers · ${nShots} real captured pages · click any title to open the source · grouped by company` },
    draggable: false, selectable: false,
  });

  const COL_Y = Y + 1180;
  COMPANIES.forEach((c, ci) => {
    const x = X0 + ci * COL_PITCH;
    out.push({
      id: `artcol-${ci}`, type: "articleColHeader", position: { x, y: COL_Y },
      data: { company: c.company, tag: c.tag, blurb: c.blurb, count: c.articles.length, w: W },
      draggable: false, selectable: false,
    });
    c.articles.forEach((a, ai) => {
      out.push({
        id: `art-${ci}-${ai}`, type: "articleShot",
        position: { x, y: COL_Y + HEADER_H + ai * ROW_PITCH },
        data: { title: a.title, url: a.url, img: a.img, domain: a.domain, w: W },
        draggable: false, selectable: false,
      });
    });
  });

  return out;
}
